import React, { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from 'store'
import {
  CursorPointer,
  Flex,
  MainHeadline,
  PageFooter,
  PageWrapper,
  Tabs,
  TabsContainer,
} from 'styling/GlobalStyles'
import { OtherText, TitleText } from 'styling/Typography'
import { toast } from 'react-toastify'
import { CloseIcon } from 'assets/icons/CloseIcon'
import { Input } from 'components/Input/Input'
import { Modal } from 'components/Modal/Modal'
import { tokens } from 'styling/tokens'
import { NavBar } from 'components/NavBar/NavBar'
import { NoItems } from 'components/NoItems-fallback/NoItems'
import { Pagination } from 'components/Pagination/Pagination'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { MetricCard } from 'components/ui/MetricCard/MetricCard'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { Button } from 'components/ui/Button/Button'
import { DemoBadge } from 'pages/Operations/Shared/OpsSharedComponents'
import { USE_OPS_MOCKS } from 'pages/Operations/Shared/opsControl'
import { FilterEnum, LinkEnum } from 'components/Filter/SingleFilter'
import useQueryPagination from 'hooks/useQueryPagination'
import { getParams, handlePagination } from 'utils/helpers'
import {
  clearInventoryDashboard,
  getInventoryItemsDashboard,
} from 'features/inventory/inventoryItemsSlice'
import { IInventoryItemsDashboardSingleResponse } from 'types/api/inventoryItems.interface'
import { IInvLocalReservation, IInvStockRowVM, IInvStockViewTab } from 'types/inventoryStock.interface'
import {
  formatInvGhs,
  formatInvGhsShort,
  INV_DEMO_ITEMS,
  INV_KPIS,
  INV_LOCATION_SPLIT_LABEL,
  INV_LOCATION_VALUES,
  INV_RESERVED_COMMITMENTS_LABEL,
  INV_RESERVED_VALUE_GHS,
  INV_SITE_STOCK_VALUE_GHS,
  INV_STOCK_AVAILABLE_CAPTION,
  invAvailableQty,
  invBelowMin,
  invItemStatus,
  invLastMovementLabel,
  invNatureLabel,
  invReservationsLabel,
  invReservedQty,
  invShortLocation,
  invSlowMovers,
  invStockSplitLabel,
  invStockValueGhs,
  invTotalOnHand,
  invUnitsHint,
  invWorstStockoutLabel,
} from '../inventoryDemoData'
import {
  StockAvailableValue,
  StockCellMeta,
  StockCellName,
  StockCellText,
  StockCommitNote,
  StockKpiStrip,
  StockNameLine,
  StockNumText,
  StockOnHandValue,
  StockReservedValue,
} from './InventoryStock.styled'

const STOCK_VIEWS: IInvStockViewTab[] = [
  { id: 'all', label: 'All' },
  { id: 'low', label: 'Low stock' },
  { id: 'critical', label: 'Critical' },
  { id: 'slow', label: 'Slow moving' },
]

const realBelowMin = (item: IInventoryItemsDashboardSingleResponse): boolean =>
  Boolean(item.isLowStock) ||
  (item.quantity != null && item.minLevel != null && item.quantity < item.minLevel)

export const InventoryStock = () => {
  const { dashboard } = useSelector((store: RootState) => store.inventoryItems)
  const [params, setParams] = useState<any>(getParams({ location, limit: 30 }))
  const [view, setView] = useState<IInvStockViewTab['id']>('all')

  useQueryPagination({
    fetchFunctions: [getInventoryItemsDashboard],
    cleanUpFunctions: [clearInventoryDashboard],
    params,
  })

  const realItems = dashboard?.data ?? []
  const [demoPreview, setDemoPreview] = useState(false)
  const usingDemo = (!realItems.length || demoPreview) && USE_OPS_MOCKS

  const [localReservations, setLocalReservations] = useState<IInvLocalReservation[]>([])
  const [reserveRow, setReserveRow] = useState<IInvStockRowVM | null>(null)
  const [reserveQty, setReserveQty] = useState('')
  const [reserveFor, setReserveFor] = useState('')

  const localReservedFor = (sku: string): number =>
    localReservations.filter((r) => r.sku === sku).reduce((sum, r) => sum + r.qty, 0)

  const localReservationsLabel = (sku: string): string | null => {
    const mine = localReservations.filter((r) => r.sku === sku)
    if (!mine.length) return null
    return mine.map((r) => `${r.reservedFor} — ${r.qty}`).join(' · ')
  }

  const handleReserve = () => {
    if (!reserveRow) return
    const qty = Number(reserveQty)
    if (!qty || qty <= 0) {
      toast.warn('Enter a quantity greater than zero')
      return
    }
    if (reserveRow.available != null && qty > reserveRow.available) {
      toast.warn(`Only ${reserveRow.available} available — reservation cannot exceed it`)
      return
    }
    if (!reserveFor.trim()) {
      toast.warn('Say what the stock is reserved for')
      return
    }
    setLocalReservations((prev) => [
      ...prev,
      { sku: reserveRow.sku, qty, reservedFor: reserveFor.trim() },
    ])
    toast.success(`Reserved ${qty} × ${reserveRow.name} — ${reserveFor.trim()}`)
    setReserveRow(null)
    setReserveQty('')
    setReserveFor('')
  }

  const rows = useMemo<IInvStockRowVM[]>(() => {
    if (usingDemo) {
      let demoItems = INV_DEMO_ITEMS
      if (view === 'low') demoItems = INV_DEMO_ITEMS.filter(invBelowMin)
      if (view === 'critical') demoItems = INV_DEMO_ITEMS.filter((item) => item.critical)
      if (view === 'slow') {
        demoItems = INV_DEMO_ITEMS.filter((item) => item.lastMovementDaysAgo > 180)
      }
      return demoItems.map((item) => {
        const status = invItemStatus(item)
        return {
          id: item.id,
          name: item.name,
          sku: item.sku,
          onHand: invTotalOnHand(item),
          splitLabel: invStockSplitLabel(item),
          minLevel: item.minLevel,
          valueLabel: formatInvGhs(invStockValueGhs(item)),
          belowMin: invBelowMin(item),
          critical: item.critical,
          statusLabel: status.label,
          statusTone: status.tone,
          lastMovementLabel: invLastMovementLabel(item.lastMovementDaysAgo),
          natureLabel: invNatureLabel(item.nature),
          reserved: invReservedQty(item.sku) + localReservedFor(item.sku),
          available: Math.max(invAvailableQty(item) - localReservedFor(item.sku), 0),
          reservationsLabel:
            [invReservationsLabel(item.sku), localReservationsLabel(item.sku)]
              .filter(Boolean)
              .join(' · ') || null,
          unitsHint: invUnitsHint(item.sku),
        }
      })
    }
    const filteredReal =
      view === 'all' ? realItems : view === 'low' ? realItems.filter(realBelowMin) : []
    return filteredReal.map((item) => {
      const belowMin = realBelowMin(item)
      return {
        id: item.id,
        name: item.name,
        sku: item.sku,
        onHand: item.quantity,
        splitLabel: null,
        minLevel: item.minLevel,
        valueLabel: null,
        belowMin,
        critical: false,
        statusLabel: belowMin ? 'BELOW MIN' : 'Ok',
        statusTone: belowMin ? 'danger' : 'success',
        lastMovementLabel: null,
        natureLabel: null,
        reserved: null,
        available: null,
        reservationsLabel: null,
        unitsHint: null,
      }
    })
  }, [usingDemo, realItems, view, localReservations])

  const filters = [
    {
      type: FilterEnum.ASYNC_SELECT,
      name: 'Location',
      link: LinkEnum.LOCATIONS,
      propName: 'locationId',
      single: true,
    },
    {
      type: FilterEnum.ASYNC_SELECT,
      link: LinkEnum.INVENTORY_CATEGORIES,
      name: 'Category',
      state: null,
      single: true,
      propName: 'categoryId',
    },
  ]

  const columns: IHonestTableColumn<IInvStockRowVM>[] = [
    {
      id: 'item',
      header: 'ITEM',
      fill: true,
      cell: (row) => (
        <StockCellText>
          <StockNameLine>
            <StockCellName>{row.name}</StockCellName>
            {row.natureLabel && <StatusPill status='neutral'>{row.natureLabel}</StatusPill>}
          </StockNameLine>
          <StockCellMeta>{row.sku}</StockCellMeta>
          {row.unitsHint && <StockCellMeta>{row.unitsHint}</StockCellMeta>}
        </StockCellText>
      ),
    },
    {
      id: 'onHand',
      header: 'ON HAND',
      cell: (row) => (
        <StockCellText>
          <StockOnHandValue $belowMin={row.belowMin}>{row.onHand}</StockOnHandValue>
          {row.splitLabel && <StockCellMeta>{row.splitLabel}</StockCellMeta>}
        </StockCellText>
      ),
    },
    {
      id: 'reserved',
      header: 'RESERVED',
      cell: (row) =>
        row.reserved != null ? (
          <StockReservedValue $active={row.reserved > 0} title={row.reservationsLabel ?? undefined}>
            {row.reserved}
          </StockReservedValue>
        ) : (
          <OtherText grey>—</OtherText>
        ),
    },
    {
      id: 'available',
      header: 'AVAILABLE',
      cell: (row) =>
        row.available != null ? (
          <StockCellText title={row.reservationsLabel ?? undefined}>
            <StockAvailableValue $committed={row.available === 0 && row.onHand > 0}>
              {row.available}
            </StockAvailableValue>
            {row.available === 0 && row.onHand > 0 && <StockCommitNote>fully committed</StockCommitNote>}
          </StockCellText>
        ) : (
          <OtherText grey>—</OtherText>
        ),
    },
    { id: 'min', header: 'MIN', cell: (row) => <StockNumText>{row.minLevel}</StockNumText> },
    {
      id: 'value',
      header: 'VALUE',
      align: 'right',
      cell: (row) =>
        row.valueLabel ? <StockNumText>{row.valueLabel}</StockNumText> : <OtherText grey>—</OtherText>,
    },
    {
      id: 'status',
      header: 'STATUS',
      cell: (row) => (
        <Flex center gap='0.5rem'>
          <StatusPill status={row.statusTone} dot={row.statusTone === 'danger'}>
            {row.statusLabel}
          </StatusPill>
          {row.critical && <StatusPill status='accent'>Critical</StatusPill>}
        </Flex>
      ),
    },
    {
      id: 'reserve',
      header: '',
      align: 'right',
      cell: (row) =>
        row.reserved != null ? (
          <Button variant='secondary' size='sm' onClick={() => setReserveRow(row)}>
            Reserve
          </Button>
        ) : (
          <OtherText grey>—</OtherText>
        ),
    },
    {
      id: 'lastMovement',
      header: 'LAST MOVEMENT',
      cell: (row) =>
        row.lastMovementLabel ? (
          <StockNumText>{row.lastMovementLabel}</StockNumText>
        ) : (
          <OtherText grey>—</OtherText>
        ),
    },
  ]

  return (
    <>
      <NavBar
        defaultValue={params.q}
        input
        placeholder='Search stock...'
        setParams={setParams}
        filterOptions={filters}
        enableTemporaryManualClosing
      />
      <PageWrapper>
        <Flex between center mb='16'>
          <Flex column gap='0.25rem'>
            <Flex center gap='0.75rem'>
              <MainHeadline>Stock on hand</MainHeadline>
              {usingDemo && <DemoBadge />}
            </Flex>
            <OtherText grey>
              Quantities and value across stores — the item registry lives under Items.
            </OtherText>
          </Flex>
          {Boolean(realItems.length) && USE_OPS_MOCKS && (
            <Button
              variant='secondary'
              size='sm'
              onClick={() => setDemoPreview((prev) => !prev)}
            >
              {demoPreview ? 'Back to live data' : 'Preview demo world'}
            </Button>
          )}
        </Flex>
        {usingDemo && (
          <StockKpiStrip>
            <MetricCard
              value={formatInvGhsShort(INV_SITE_STOCK_VALUE_GHS)}
              label='STOCK VALUE'
              sublabel={INV_LOCATION_SPLIT_LABEL}
              accentColor={tokens.accent.base}
            />
            <MetricCard
              value={formatInvGhsShort(INV_RESERVED_VALUE_GHS)}
              label='RESERVED'
              sublabel={INV_RESERVED_COMMITMENTS_LABEL}
              accentColor={tokens.accent.onDark}
            />
            <MetricCard
              value={INV_KPIS.belowMinCount}
              label='ITEMS BELOW MIN'
              sublabel={invWorstStockoutLabel()}
              accentColor={tokens.status.danger.fg}
            />
            <MetricCard
              value={invSlowMovers(INV_DEMO_ITEMS).length}
              label='SLOW / OBSOLETE'
              sublabel='No movement in 6+ months'
              accentColor={tokens.status.warning.fg}
            />
            <MetricCard
              value={INV_LOCATION_VALUES.length}
              label='LOCATIONS'
              sublabel={INV_LOCATION_VALUES.map((l) => invShortLocation(l.location)).join(' · ')}
              accentColor={tokens.status.info.fg}
            />
          </StockKpiStrip>
        )}
        <Flex mb='16'>
          <TabsContainer>
            {STOCK_VIEWS.map((v) => (
              <Tabs key={v.id} active={view === v.id} onClick={() => setView(v.id)}>
                {v.label}
              </Tabs>
            ))}
          </TabsContainer>
        </Flex>
        {!rows.length ? (
          <NoItems title='Stock on hand' />
        ) : (
          <>
            <HonestTable columns={columns} data={rows} keyField='id' />
            <Flex mt='8'>
              <OtherText grey>{INV_STOCK_AVAILABLE_CAPTION}</OtherText>
            </Flex>
          </>
        )}
      </PageWrapper>
      {!usingDemo && (
        <PageFooter>
          <Pagination
            setPage={(page) => handlePagination(page, setParams, 30)}
            pageCount={Math.ceil(dashboard.totalPages)}
            forcePage={(params.page ? params.page : 1) - 1}
          />
        </PageFooter>
      )}
      {reserveRow && (
        <Modal black>
          <Flex between center mb='16'>
            <TitleText weight='bold'>Reserve stock — {reserveRow.name}</TitleText>
            <CursorPointer onClick={() => setReserveRow(null)}>
              <CloseIcon />
            </CursorPointer>
          </Flex>
          <OtherText grey>
            Available now: {reserveRow.available} of {reserveRow.onHand} on hand
          </OtherText>
          <Input
            type='number'
            labelText='Quantity'
            value={reserveQty}
            name='reserveQty'
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setReserveQty(e.target.value)}
            placeholder='0'
          />
          <Input
            labelText='Reserved for'
            value={reserveFor}
            name='reserveFor'
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setReserveFor(e.target.value)}
            placeholder='WO-4510 — crusher liner change'
          />
          <OtherText grey>
            Reservations are usually created by approving a request — this manual hold is
            session-local until the backend reservation endpoint lands.
          </OtherText>
          <Flex justend mt='16'>
            <Button variant='primary' onClick={handleReserve}>
              Reserve
            </Button>
          </Flex>
        </Modal>
      )}
    </>
  )
}
